import Constants from '~/config/Constants';
import { isCustomLoggingEnabled, isNonProd } from './EnvUtils';
import breadcrumb from './breadcrumb';

// vuex logger with special powers

const ENABLE_FILTERS = false;
const WAIT_TIME = 500;

const disallowedActions = [
  'root/localClientInit',
  'root/getRenderMetaData',
  'root/setDeviceProperties',
  'root/setDeviceInfoFromClient',
  'root/createGlobalFunctions',
  'root/setUser',
  'root/setLifecycleData',
  'root/setFeature',
  'searchState/setSearchSource',
  'root/setGameSource',
  'root/showHelpButton',
  'currentUser/setReferralCampaigns',
  'root/setUnreadNotificationCount',
  'currentUser/setUserTags',
  'uiManager/onShowSearchBarQuizPage',
  'currentQuiz/setCoAuthors',
  'onboardingTooltip/show',
  'currentUser/setRecommendationMeta',
  'contentEditor/setQuizGenFilters',
];

const disallowedMutations = [
  'root/DO_CLIENT_INITIATION',
  'root/SET_DEVICE_PROPERTIES',
  'root/SET_DEVICE_INFO',
  'root/serverData',
  'root/user',
  'root/setTranslations',
  'root/setLifecycleData',
  'root/SET_FEATURES',
  'searchState/SET_SEARCH_SOURCE',
  'root/SET_GAME_SOURCE',
  'currentUser/SET_REFERRAL_CAMPAIGNS',
  'root/setUnreadNotificationCount',
  'currentUser/SET_USER_TAGS',
  'uiManager/onShowSearchBarQuizPage',
  'uiManager/TOGGLE_VISIBLITY_SEARCH_BAR_QUIZ_PAGE',
  'currentQuiz/SET_COAUTHORS',
  'onboardingTooltip/resetCounter',
  'onboardingTooltip/setTotalCount',
  'currentUser/SET_RECOMMENDATION_META',
  'contentEditor/SET_QUIZ_GEN_FILTERS',
];

// to bypass the eslint warning
const logger = console;

function parseObserver(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    return obj;
  }
}

// helps you inspect the state between UI interactions
function handleStoreInactivity() {
  let wasRunning = false;
  let runningTimer = null;
  const logInactivity = () => {
    wasRunning = true;
    clearTimeout(runningTimer);
    runningTimer = setTimeout(() => {
      if (wasRunning) {
        logger.log(`%c ************ store idle for ${WAIT_TIME}ms ************`, 'color: red; font-size: 16px');
        wasRunning = false;
      }
    }, WAIT_TIME);
  };
  return logInactivity;
}
const logInactivity = handleStoreInactivity();

function mutationsLogger(store) {
  store.subscribe((mutation, state) => {
    if (!mutation) {
      return;
    }
    let skipLogging = false;
    if (ENABLE_FILTERS) {
      skipLogging = (disallowedMutations.includes(mutation.type) || mutation.type.toLowerCase().includes('tiptap') || mutation.type.toLowerCase().includes('analytics'));
    }
    if (!skipLogging) {
      logInactivity();
      const stateKey = mutation.type.split('/')[0];
      logger.group(`%c mutation: ${mutation.type}`, 'color: #c41bf7');
      logger.log('%c payload:', 'color: #19800e', parseObserver(mutation.payload));
      logger.log('%c current state:', 'color: #19800e', parseObserver(state[stateKey]));
      logger.log('%c root state:', 'color: #19800e', state);
      logger.groupEnd();
    }
  });
}

function actionsLogger(store) {
  // no array function for better trace

  store.subscribeAction((action, state) => {
    if (!action) {
      return;
    }
    let skipLogging = false;
    if (ENABLE_FILTERS) {
      skipLogging = disallowedActions.includes(action.type) || action.type.toLowerCase().includes('tiptap') || action.type.toLowerCase().includes('analytics');
    }
    if (!skipLogging) {
      logInactivity();
      const stateKey = action.type.split('/')[0];
      logger.groupCollapsed(`%c action: ${action.type}`, 'color: #03A9F4');
      logger.log('%c payload:', 'color: #19800e', parseObserver(action.payload));
      logger.log('%c current state:', 'color: #19800e', parseObserver(state[stateKey]));
      logger.log('%c root state:', 'color: #19800e', state);
      // find out "Who Updated My Store"
      logger.groupCollapsed('%c call-stack of action', 'color: #9c5c19');
      logger.trace();
      logger.groupEnd();

      logger.groupEnd();
    }
  });
}

const allowedActionsBreadcrumb = [
  'contentEditor/saveQuizQuestion',
  'contentEditor/createQuizQuestion',
  'contentEditor/updateQuizQuestionWithoutDrafts',
  'contentEditor/updateQuizQuestion',
  'currentQuiz/updateQuestion',
  'contentEditor/addNewSlide',
  'slideEditor/resetSlideEditor',
];

function actionsBreadcrumb(store) {
  store.subscribe((action, state) => {
    if (!action) {
      return;
    }
    if (state.contentEditor?.quiz?.type !== Constants.ContentType.QUIZ) {
      return;
    }
    const loggingDisabled = !allowedActionsBreadcrumb.includes(action.type) || action.type.toLowerCase().includes('tiptap') || action.type.toUpperCase().includes('HELP_BUTTON') || action.type.toLowerCase().includes('analytics');
    if (!loggingDisabled) {
      breadcrumb().addAction(action.type);
    }
  });
}

const disallowedMutationsBreadcrumb = [
  'slideEditor/UPDATE_QUESTION_OPTIONS',
];

function mutationsBreadcrumb(store) {
  store.subscribe((mutation, state) => {
    if (!mutation) {
      return;
    }
    if (state.contentEditor?.quiz?.type !== Constants.ContentType.QUIZ) {
      return;
    }
    const loggingDisabled = disallowedMutationsBreadcrumb.includes(mutation.type) || mutation.type.toLowerCase().includes('tiptap') || mutation.type.toUpperCase().includes('HELP_BUTTON') || mutation.type.toLowerCase().includes('analytics');
    if (!loggingDisabled) {
      breadcrumb().addMutation(mutation.type);
    }
  });
}

function contentLogger(store) {
  actionsBreadcrumb(store);
  mutationsBreadcrumb(store);
  if (isCustomLoggingEnabled() && isNonProd()) {
    mutationsLogger(store);
    actionsLogger(store);
  }
}

export default contentLogger;
